import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from 'store';

import DrawBtn from '../ButtonDiv/Facet/DrawBtn';
import EditFacet from '../ButtonDiv/Facet/EditFacet';
import EditToolOptions from '../ButtonDiv/Facet/SolarToolEditOption';
import AddPanelButton from '../ButtonDiv/Panel/AddPanelButton';
import DeletePanelBtn from '../ButtonDiv/Panel/DeletePanelBtn';
import MovePanelButton from '../ButtonDiv/Panel/MovePanelButton';
import DoneBtn from '../ButtonDiv/CreateBtn';
import { FacetPanelWrap, ToolbarLabel, EditToolOptionsDiv, PanelToolWrap, BtnWrap, CalculatebtnWrap, FacetTranslateInfoText } from '../style';
import { selectActiveAction, TOOL_ACTION } from 'store/selectors/toolSelector';
import { ToolCancelButton } from '../ButtonDiv/CancelButton/cancelButton';
import { findIntersection } from '../../../../util';
import { ALL_ACTIONS, AZIMUTH_CONSTRAINT_DEFAULT_MODE, PANEL_OPS, SOLAR_FACET_OPS, SOLAR_FACET_OPS_WITH_BUTTON_ON_UI } from '../../../../constants';
import withPermission from 'components/hoc/withPermission';
import KeyboardShortcutsWarningModal from '../KeyboardShortcutsWarningModal/KeyboardShortcutsWarningModal';
import { useEffect } from 'react';
import { resetAzimuthEditState, setEditedAzimuthFacet, setInvalidAzimuthFacet, setRoofIndexes, 
	setSelectedRoofAzimuth, setSelectedRoofIndex, updateTransition } from 'store/slices/ToolSlice';
import { resetAllTransformations } from 'components/tool/utils';
import { changePanel } from 'store/slices/RoofDataSlice/roofDataSlice';
import { clearToolModeHandlers, getToolModeHandlers, registerToolModeHandlers } from '../ToolRegistry';
import { useRefs } from 'contexts/RefContext';
import useTranslatePanelPositionsMutation from 'hooks/api/facet/useTranslateFacet';
import useStartShadingRecalc from 'hooks/useStartShadingRecalc';
import { isInHouseSimulationMode } from '../../../../util';
import { hasShadingAffectingTransform } from '../util';
import TreeEditModeBtn from '../../../Tree/TreeEditModeBtn';

export default function SolarEditorTool(){
	// const { editModeEnabled, deletedRoofIndexes, drawModeEnabled, roofIndexes, deleteObstructionsMode, newlyCreatedFacets, hadTransition, 
	// 	addObstructionsMode } = useSelector((state: RootState) => state.toolNewPostions.data);
	// const { activePanelMode } = useSelector((state: RootState) => state.panelSlice.data);
	// const { enableEditFacet} = useSelector((state: RootState) => state.roofEditData.data);
	// const { activeRoofTool } = useSelector((state: RootState) => state.roofToolLines.data);
	const { deletedRoofIndexes, roofIndexes, selectedRoofIndex, anyInvalidAzimuth } = useSelector((state: RootState) => state.toolNewPostions.data);

	// const isAnyActionActive = useSelector(selectIsAnyToolActionActive);
	const activeAction = useSelector(selectActiveAction);
	// const hasUserMadeModificaiton = !!deletedRoofIndexes?.length || !!Object.keys(newlyCreatedFacets)?.length || !!roofIndexes?.length;
	// const showDoneBtnIfUserHasModified = editModeEnabled && (hasUserMadeModificaiton || activePanelMode || drawModeEnabled 
	// 	|| deleteObstructionsMode || enableEditFacet || addObstructionsMode || activeRoofTool);
	// const showCanelBtnInIndividualPanelFnMOde= editModeEnabled && (drawModeEnabled || deleteObstructionsMode || activePanelMode 
	// 	|| hadTransition || enableEditFacet || activeRoofTool);
	const allowedActions = useSelector((state: RootState) => state.roleState.data.allowedActions);

	const anyPanelOps = !!findIntersection(PANEL_OPS, allowedActions).length; 
	const anyFacetOps = !!findIntersection(SOLAR_FACET_OPS, allowedActions).length;
	const facetOpsWithButton = findIntersection(SOLAR_FACET_OPS_WITH_BUTTON_ON_UI, allowedActions);
	const anyFacetOpsWithButton = !!facetOpsWithButton.length;
	const modesThatDontNeedDoneButton = [
		TOOL_ACTION.CHANGE_ORIENTATION,
		TOOL_ACTION.REMOVE_SHADING,
		TOOL_ACTION.RECALCULATE_LAYOUT,
		TOOL_ACTION.EDIT_CUSTOM_IMAGE
	];
	const showDoneButton = (!!activeAction && !modesThatDontNeedDoneButton.includes(activeAction)) || !!deletedRoofIndexes?.length 
		|| !!roofIndexes?.length;
	const { uuid, panel, designType, mode, allRoofSegs } = useSelector((state: RootState) => state.roofData.data);

	const { konvaRef } = useRefs();
	const dispatch = useDispatch<AppDispatch>();
	const translatePanelPositionsMutation = useTranslatePanelPositionsMutation();
	const startShadingRecalc = useStartShadingRecalc();
	const showCancelButton = (!!activeAction && !modesThatDontNeedDoneButton.includes(activeAction));

	useEffect(() => {
		const cancelHandler = () => {
			dispatch(setRoofIndexes({ roofIndexes: [] }));
			dispatch(setSelectedRoofIndex({ selectedRoofIndex: null }));
			dispatch(setSelectedRoofAzimuth({ selectedRoofAzimuth: null }));
			dispatch(updateTransition({ currentTransitionState: false }));
			dispatch(resetAzimuthEditState());

			if (konvaRef?.current) resetAllTransformations(konvaRef.current);

		};

		const doneHandler = async () => {
			if (!roofIndexes.length) {
				cancelHandler();
				return;
			}

			// snapshot before the mutation: a plain move doesn't owe a recompute, a rotation does
			const refreshShadingAfterFacetEdit = isInHouseSimulationMode(mode) && !!konvaRef?.current
				&& hasShadingAffectingTransform(konvaRef.current, roofIndexes, allRoofSegs);

			try {
				const editRes = await translatePanelPositionsMutation.mutateAsync();
				await dispatch(changePanel({ uuid, panelKey: panel.key, designType }));
				if (refreshShadingAfterFacetEdit) await startShadingRecalc(editRes);
			} finally {
				cancelHandler();
			}
		};
		registerToolModeHandlers(TOOL_ACTION.EDIT_AZIMUTH_OR_POSITION, {
			cancel: cancelHandler,
			done: doneHandler,
			cancelConfirmMessage: 'Are you sure you want to discard the translations on the facets?',
			doneConfig: {
				className: anyInvalidAzimuth ? 'azimuthInvalid' : '',
			}
		});

		return () => {
			clearToolModeHandlers(TOOL_ACTION.EDIT_AZIMUTH_OR_POSITION);
		};
	}, [allRoofSegs, anyInvalidAzimuth, designType, dispatch, konvaRef, mode, panel.key, roofIndexes, selectedRoofIndex,
		startShadingRecalc, translatePanelPositionsMutation, uuid]);

	const { useEdgeTypeBasedSetbacks } = useSelector((s:RootState)=>s.designSettigs.data);
	if(!anyFacetOps && !anyPanelOps){
		return <></>;
	}
	// NOTE: incase of only edit facet props action is allowed with or without toggle panels we need to show an info text when we press edit button 
	// and remove it as soon as any other button shows up in tool bar. Below is how the alloweActions may look like in this case
	// allowedActions = [SWITCH_DESIGN_MODE, TRANSLATE_PANELS_TO_NEW_POSITION, UPDATE_ROOF_PITCH, ...(roof ops)]
	// allowedActions = [SWITCH_DESIGN_MODE, TRANSLATE_PANELS_TO_NEW_POSITION, UPDATE_ROOF_PITCH, TOGGLE_PANELS, ...(roof ops)]
	const onlyTranslatePanelsAllowed = !anyPanelOps && !(showDoneButton || showCancelButton ) && (
		(!anyFacetOpsWithButton && allowedActions.includes(ALL_ACTIONS.TRANSLATE_PANELS_TO_NEW_POSITION) 
																&& allowedActions.includes(ALL_ACTIONS.UPDATE_ROOF_PITCH)) ||
		(!anyFacetOpsWithButton && allowedActions.includes(ALL_ACTIONS.TRANSLATE_PANELS_TO_NEW_POSITION) 
																&& allowedActions.includes(ALL_ACTIONS.UPDATE_ROOF_PITCH)
																&& allowedActions.includes(ALL_ACTIONS.TOGGLE_PANELS)));
	const PermittedDrawBtn = withPermission(DrawBtn, ALL_ACTIONS.EDIT_ROOF_SEGMENTS);
	const PermittedEditFacet = withPermission(EditFacet, ALL_ACTIONS.UPDATE_FACETS_COORDS);
	const PermittedEditAddPanelBtn = withPermission(AddPanelButton, ALL_ACTIONS.ADD_PANELS_EXTERIOR_COORDS);
	const PermittedEditMovePanelButton = withPermission(MovePanelButton, ALL_ACTIONS.TRANSFORM_PANELS);
	const PermittedEditDeletePanelBtn = withPermission(DeletePanelBtn, ALL_ACTIONS.REMOVE_PANELS);
	const PermittedTreeEditing = withPermission(TreeEditModeBtn, ALL_ACTIONS.REMOVE_PANELS);
	return (

		<>
			{anyFacetOpsWithButton ? <FacetPanelWrap className='toolbar-btn-wrap a'>
				<ToolbarLabel>Facets</ToolbarLabel>
				<EditToolOptionsDiv className='toolfacet-buttonwrap'>
					<PermittedDrawBtn/>
					<PermittedEditFacet/>
					<EditToolOptions />
					<PermittedTreeEditing/>
				</EditToolOptionsDiv>
			</FacetPanelWrap> : <></>}
			{anyPanelOps ? <PanelToolWrap className='toolbar-btn-wrap'>
				<ToolbarLabel>Panels</ToolbarLabel>
				<EditToolOptionsDiv>
					{/* <PanelwithOreintation> */}
					<PermittedEditAddPanelBtn/>
					{/* </PanelwithOreintation> */}
					<PermittedEditMovePanelButton/>
					<PermittedEditDeletePanelBtn/>
				</EditToolOptionsDiv>

			</PanelToolWrap>: <></>}
			{onlyTranslatePanelsAllowed ? <FacetTranslateInfoText>Click on a facet to adjust array</FacetTranslateInfoText> : <></>}
			{(showDoneButton || showCancelButton) && <CalculatebtnWrap>
				{showDoneButton && <BtnWrap><DoneBtn /></BtnWrap>}
				{/* {showDoneButton && <BtnWrap><ToolDoneButton /></BtnWrap>} */}
				{showCancelButton && <ToolCancelButton id="solar-action-cancel"/>}
			</CalculatebtnWrap>}
			<KeyboardShortcutsWarningModal/>
		</>
	);
}
